import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
@Injectable()
export class AuthService
{
    private _authenticated: boolean = false;
    public user;
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('access_token', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('access_token') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string, password: string, code: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }
        let authForm = new FormData();
        authForm.append("email", credentials.email);
        authForm.append("password", credentials.password);
        if(credentials.code){
            authForm.append("code", credentials.code)
        }
        return this._httpClient.post(`${environment.API_URL}/auth/login`, authForm).pipe(
            switchMap((response: any) => {
                
                if(response.status == 200){
                    // Store the access token in the local storage
                    this.accessToken = response.data.token;
                    
                    // Set the authenticated flag to true
                    this._authenticated = true;
                    if(!response.data.id_user){
                        this.signInUsingToken().subscribe();
                    }
                    
                    // Store the user on the user service
                    this.user = response.data;
                    
                    // Return a new observable with the response
                    return of(response);
                } else {
                    return of(response);
                }
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {   
        // Renew token
        let headers = new HttpHeaders();
        let _headers = headers.append('Authorization', `Bearer ${this.accessToken}`);
        return this._httpClient.post(`${environment.API_URL}/auth/get-user-token`, null, {
            headers: _headers
        }).pipe(
            catchError(() => {

                // Return false
                return of(false);
            }),
            switchMap((response: any) => {
                //console.log("Access", response)
                // Store the access token in the local storage
                //this.accessToken = response.access_token;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this.user = response.data;
                console.log(this.user)

                // Return true
                return of(true);
            })
        );
    }

    getQrCode(email){
        return this._httpClient.get(`${environment.API_URL}/auth/get-code?email=${email}`)
    }

    loginTwoFactor(form){
        return this._httpClient.get(`${environment.API_URL}/auth/activate-code?email=${form.email}&code=${form.code}&secret=${form.secret}&password=${form.password}`)
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('access_token');
        window.location.replace('sign-in');
        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string, email: string, password: string, company: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string, password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
